import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Building2, UserRound, ShieldCheck, Globe2 } from "lucide-react";
import { useSettings, SectionHeader, SaveBar, TextField, TextArea, Toggle, LogoUploader } from "./shared";
import SearchSelect from "./SearchSelect";
import { countryOptions, timezoneOptions, suggestedZones } from "../../../lib/geo";

const EMPTY_PERSONAL = { first_name: "", last_name: "", role: "", email: "", phone: "" };
const EMPTY_INSURANCE = { insurer: "", policy_number: "", coverage: "", description: "", show_on_shop: true };

export default function CompanySection() {
  const { settings, loading, reload } = useSettings();
  const [tab, setTab] = useState("company");
  const [local, setLocal] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (settings) {
      setLocal(settings.company);
      setPersonal({ ...EMPTY_PERSONAL, ...(settings.personal || {}) });
      setInsurance({ ...EMPTY_INSURANCE, ...(settings.insurance || {}) });
    }
  }, [settings]);
  const countryOpts = useMemo(() => countryOptions(), []);
  const tzOpts = useMemo(() => timezoneOptions(local?.country || ""), [local?.country]);
  if (loading || !local || !personal || !insurance) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  const dirtyCompany = JSON.stringify(local) !== JSON.stringify(settings.company);
  const dirtyPersonal = JSON.stringify(personal) !== JSON.stringify({ ...EMPTY_PERSONAL, ...(settings.personal || {}) });
  const dirtyInsurance = JSON.stringify(insurance) !== JSON.stringify({ ...EMPTY_INSURANCE, ...(settings.insurance || {}) });
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const setCountry = (code) => setLocal((p) => {
    const zones = suggestedZones(code);
    const tz = zones.length && !zones.includes(p.timezone) ? zones[0] : p.timezone;
    return { ...p, country: code, timezone: tz };
  });
  const setP = (k, v) => setPersonal((p) => ({ ...p, [k]: v }));
  const setI = (k, v) => setInsurance((p) => ({ ...p, [k]: v }));

  const saveCompany = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { company: local }); toast.success("Informations entreprise enregistrées"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  const savePersonal = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { personal }); toast.success("Informations personnelles enregistrées"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  const saveInsurance = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { insurance }); toast.success("Assurance enregistrée"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  const TABS = [
    { key: "company", label: "Entreprise", icon: Building2 },
    { key: "personal", label: "Informations personnelles", icon: UserRound },
    { key: "insurance", label: "Assurance", icon: ShieldCheck },
  ];

  return (
    <div className="max-w-3xl">
      <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 mb-5" data-testid="company-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-lg text-xs font-semibold transition-colors ${tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={`company-tab-${t.key}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "company" ? (
        <>
          <div className="flex items-start justify-between gap-4">
            <SectionHeader overline="Entreprise" title="Identité de votre société" subtitle="Ces informations apparaissent sur vos devis, contrats et factures. Elles rassurent vos clients et sont exigées légalement." />
            <div className="shrink-0 rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="company-account-options">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Options du compte</div>
              <div>
                <div className="text-[10px] font-semibold text-slate-500 mb-0.5">Format de date</div>
                <select value={local.date_format || "DD/MM/YYYY"} onChange={(e) => set("date_format", e.target.value)} className="w-40 h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs" data-testid="company-date-format">
                  <option value="DD/MM/YYYY">JJ/MM/AAAA</option>
                  <option value="DD.MM.YYYY">JJ.MM.AAAA</option>
                  <option value="YYYY-MM-DD">AAAA-MM-JJ</option>
                  <option value="MM/DD/YYYY">MM/JJ/AAAA</option>
                </select>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6" data-testid="company-section">
            <LogoUploader value={local.logo_data_url} onChange={(v) => set("logo_data_url", v)} testid="company-logo" />
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Nom de l'entreprise" value={local.name} onChange={(v) => set("name", v)} placeholder="Paris Luxury Cars" testid="company-name" />
              <TextField label="Forme juridique" value={local.legal_form} onChange={(v) => set("legal_form", v)} placeholder="SASU, SARL, EI…" testid="company-legal-form" />
              <TextField label="N° d'enregistrement (SIRET)" value={local.registration_number} onChange={(v) => set("registration_number", v)} placeholder="123 456 789 00012" testid="company-registration" />
              <TextField label="N° TVA intracommunautaire" value={local.vat_number} onChange={(v) => set("vat_number", v)} placeholder="FR 12 345678901" testid="company-vat-number" hint="Optionnel si TVA non applicable." />
            </div>
            <TextField label="Adresse du siège" value={local.address} onChange={(v) => set("address", v)} placeholder="12 rue de la Paix, 75002 Paris" testid="company-address" />
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Téléphone" value={local.phone} onChange={(v) => set("phone", v)} placeholder="01 23 45 67 89" testid="company-phone" />
              <TextField label="Email de contact" value={local.email} onChange={(v) => set("email", v)} placeholder="contact@monentreprise.fr" testid="company-email" />
              <TextField label="Site web" value={local.website} onChange={(v) => set("website", v)} placeholder="www.monentreprise.fr" testid="company-website" />
            </div>
            <div className="pt-5 border-t border-slate-100 space-y-4" data-testid="company-localization">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-violet-50 text-violet-600"><Globe2 className="h-4 w-4" /></span>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Localisation</div>
                  <div className="text-xs text-slate-500">Pays et fuseau horaire de votre activité. Le fuseau fiabilise l'envoi de vos rappels automatiques.</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Pays / territoire</label>
                  <SearchSelect
                    value={local.country || ""}
                    onChange={setCountry}
                    options={countryOpts}
                    placeholder="Sélectionner un pays…"
                    searchPlaceholder="Rechercher un pays…"
                    emptyLabel="Aucun pays trouvé"
                    testid="company-country"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Fuseau horaire</label>
                  <SearchSelect
                    value={local.timezone || ""}
                    onChange={(v) => set("timezone", v)}
                    options={tzOpts}
                    placeholder="Sélectionner un fuseau…"
                    searchPlaceholder="Rechercher une ville, un fuseau…"
                    emptyLabel="Aucun fuseau trouvé"
                    testid="company-timezone"
                  />
                  <p className="mt-1.5 text-[11px] text-slate-400">Décalage UTC affiché pour l'heure actuelle (été/hiver gérés automatiquement).</p>
                </div>
              </div>
            </div>
          </div>
          <SaveBar dirty={dirtyCompany} saving={saving} onSave={saveCompany} testid="company-save" />
        </>
      ) : tab === "personal" ? (
        <>
          <SectionHeader overline="Entreprise" title="Informations personnelles" subtitle="Les coordonnées de la personne responsable du compte. Utilisées pour vous contacter — jamais affichées publiquement sans votre accord." />
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6" data-testid="personal-section">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Prénom" value={personal.first_name} onChange={(v) => setP("first_name", v)} placeholder="Marc" testid="personal-first-name" />
              <TextField label="Nom" value={personal.last_name} onChange={(v) => setP("last_name", v)} placeholder="Dupont" testid="personal-last-name" />
            </div>
            <TextField label="Fonction" value={personal.role} onChange={(v) => setP("role", v)} placeholder="Gérant, Directeur d'agence…" testid="personal-role" />
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Email" value={personal.email} onChange={(v) => setP("email", v)} placeholder="marc@monentreprise.fr" testid="personal-email" />
              <TextField label="Téléphone" value={personal.phone} onChange={(v) => setP("phone", v)} placeholder="06 12 34 56 78" testid="personal-phone" />
            </div>
          </div>
          <SaveBar dirty={dirtyPersonal} saving={saving} onSave={savePersonal} testid="personal-save" />
        </>
      ) : (
        <>
          <SectionHeader overline="Entreprise" title="Assurance de la flotte" subtitle="Renseignez l'assurance de vos véhicules. Affichée sur votre page de réservation, c'est un puissant levier de confiance pour vos clients." />
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6" data-testid="insurance-section">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Compagnie d'assurance" value={insurance.insurer} onChange={(v) => setI("insurer", v)} placeholder="AXA, Allianz, MAIF…" testid="insurance-insurer" />
              <TextField label="N° de contrat" value={insurance.policy_number} onChange={(v) => setI("policy_number", v)} placeholder="123456789" testid="insurance-policy" hint="Jamais affiché publiquement." />
            </div>
            <TextField label="Type de couverture" value={insurance.coverage} onChange={(v) => setI("coverage", v)} placeholder="Tous risques, Au tiers étendu…" testid="insurance-coverage" />
            <TextArea label="Garanties incluses (affichées sur votre page)" value={insurance.description} onChange={(v) => setI("description", v)} rows={3} placeholder="Assistance 24h/24, dépannage 0 km, véhicule de remplacement…" testid="insurance-description" />
            <Toggle checked={insurance.show_on_shop} onChange={(v) => setI("show_on_shop", v)} label="Afficher l'assurance sur ma page de réservation" hint="Recommandé : rassure vos clients au moment de payer." testid="insurance-show-toggle" />
          </div>
          <SaveBar dirty={dirtyInsurance} saving={saving} onSave={saveInsurance} testid="insurance-save" />
        </>
      )}
    </div>
  );
}
